import { StripeHeader } from '@george-labs.com/design-system';

import { bankMessages } from '@shared/bank-messages-config';
import { thirdPartyProviders } from '@shared/third-party-providers-config';
import { aboutYouConfig } from '@retail/about-you-config';
import { documentsAndRequests } from '@shared/documents-and-requests-config';
import { infoApi } from '@shared/info-api-config';
import { profileConfig as profileFeatureConfig } from '@retail/profile-config';
import { featureConfigMap as loyalty } from '@retail/loyalty-stars-config';
import _ from '@luna/lodash';
import type { COUNTRY } from '@shared/core-types';
import { contactDataManagement } from '@shared/contact-data-management-config';
import { appointments } from '@retail/appointments-config';
import { gdpr } from '@retail/profile-administration-config';
import { consents } from '@retail/consents-config';
import { messenger } from '@shared/messenger-config';
import { notificationsFeatureConfig } from '@retail/notifications-config';
import { cards } from '@retail/cards-config';
import { overview } from '@retail/overview-config';
import { plugins } from '@retail/plugins-config';
import {
    AccountsCurrentFeatureConfig,
    AccountsFeatureConfig,
    BuildingSocietyFeatureConfig,
    ProductsFeatureConfig,
    SavingsFeatureConfig,
    StatementFeatureConfig,
} from '@retail/products-config';
import { exportFeatures, transactions } from '@retail/transactions-config';
import { profileSharedConfig } from '@shared/profile-config';
import { featureConfigMap as store } from '@shared/store-config';
import { navigation } from '@retail/navigation-config';
import { supportChatFeatureConfigMap } from '@shared/support-chat-config';
import { featureConfigMap as contactAreaFeatureConfigMap } from '@retail/contact-area-config';
import { securitySettings } from '@shared/security-settings-config';
import { featureConfigMap as pfm } from '@retail/pfm-config';
import { moneyAutomationFeatureConfigMap } from '@retail/money-automation-config';
import { kyc } from '@retail/kyc-config';
import type { Language } from '@luna/i18n';
import type { DateFormat } from '@luna/date';

import type { RetailFeatureConfig } from './RetailFeatureConfig';

export const baseFeatureConfig: Partial<RetailFeatureConfig> = {
    aboutYou: aboutYouConfig.base,
    appointments: appointments.base,
    bankMessages: bankMessages.base,
    contactDataManagement: contactDataManagement.base,
    documentsAndRequests: documentsAndRequests.base,
    gdpr: gdpr.base,
    consents: consents.base,
    kyc: kyc.base,
    messenger: messenger.base,
    thirdPartyProviders: thirdPartyProviders.base,
    cards: cards.base,
    overview: overview.base,
    plugins: plugins.base,
    statements: StatementFeatureConfig.base,
    transactions: transactions.base,
    export: exportFeatures.base,
    savings: SavingsFeatureConfig.base,
    supportChat: supportChatFeatureConfigMap.base,
    contactArea: contactAreaFeatureConfigMap.base,
    securitySettings: securitySettings.base,
    profileConfiguration: profileFeatureConfig.base,
    notifications: notificationsFeatureConfig.base,
    base: {
        country: 'AT' as COUNTRY,
        newAccountModel: false,
        ibanCountryCode: 'AT',
        intrabankIbanMatch: null as string | null,
        defaultLanguage: 'en' as Language,
        availableLanguages: ['en'] as Language[],
        allowedLanguages: ['en'] as Language[],
        defaultCurrency: 'EUR',
        defaultCurrencySign: '€',
        mainProductAmount: 'balance',
        loanProductAmount: null as null | 'balance',
        thousandSeparator: '.',
        displayISOCurrency: false,
        decimalSeparator: ',',
        defaultCurrencyPre: true,
        doPreCurrencyCheck: true,
        defaultPrecision: 2,
        defaultDateFormat: 'dd.MM.yyyy' as DateFormat,
        defaultDateFormatShort: 'd. MMM' as DateFormat,
        defaultDateFormatVerbose: 'd. MMMM yyyy' as DateFormat,
        defaultDateTimeFormat: 'dd.MM.yyyy HH:mm' as DateFormat,
        defaultDateTimeFullFormat: 'dd.MM.yyyy HH:mm:ss' as DateFormat,
        defaultTimezone: 'ETC/GMT',
        saveLanguageChangesToPresto: true,
        preferWebApiLanguage: false,
        sendRequestId: true,
        usingIban: true,
        unauthorizedStatusCode: '401',
        useSalutation: false,
        sendLanguageHeader: true,
        useVersionHeaders: true,
        latestVersionHeader: 'application/vnd.presto.latest+json' as string | null,
        emailValidationString:
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.toString(),
        phoneValidationString: /^\+?\d{6,}$/.toString(),
        aliasValidationStringWithoutGlobal:
            /[0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz áßäčďéěíĺľňóöőôřŕšťúůüűýžÁÄČĎÉĚÍĹĽŇÓÖŐÔŘŔŠŤÚŮÜŰÝŽ,.;:!?@&+\-*/()_#]/i.toString(),
        enableTraceWebapi: true,
        logoutIssueTimeout: 10000,
        logoutIssueLogoutType: 'logoutIssue',
        preferProductI18NNamesOnAliases: false,
        defaultStripeColor: StripeHeader.COLOR.BLUE_BRIGHT,
        showNegativeAmountsInRedColor: false,
        headers: {},
        languageOverrides: {},
        passCredentialsInRequest: false,
    },
    footer: {
        defaultInstituteId: 1,
        links: {},
    },
    directDebit: {
        enabled: false,
    },
    autoLogout: {
        timeoutInSeconds: 300,
        timerThreshold: 60,
        alertThreshold: 30,
    },
    accounts: AccountsFeatureConfig.base,
    navigation: navigation.base,
    currentAccount: AccountsCurrentFeatureConfig.base,
    accountReservations: {
        isTransactionTypeUsed: false,
        expirationDate: false,
        type: false,
    },
    buildingSociety: BuildingSocietyFeatureConfig.base,
    store: store.base,
    pfm: {
        changeCategory: true,
        additionalKeywordFields: [
            'merchantName',
            'partnerName',
            'receiverReference',
            'senderReference',
            'paymentReference',
        ],
        ...pfm.base,
    },
    settings: {
        changePassword: true,
        saveLanguageInUppercase: true,
    },
    crm: {
        sevenYearsPluginPromotion: true,
    },
    handover: {
        handoverTokenExchange: false,
        sso: false,
        sessionId: true,
    },
    loyaltyStars: loyalty.base,
    moneyAutomation: moneyAutomationFeatureConfigMap.base,
    infoApi: infoApi.base,
    profile: profileSharedConfig.base,
    tracking: {
        appName: 'GeorgeWeb_Retail_AT',
        posthog: {
            shouldTrackUserId: false,
        },
    },
    devTools: {
        alexProxyUrl: 'https://alex-proxy.beeone.at',
        apiTargetLanes: {},
        availableApiTargets: {},
        availableEnvironments: {},
        showLanguageSwitch: true,
        showSimulateTempMessage: true,
        showRgbMode: true,
    },
    alexProxyVersion: 'v2',
    reverseProxyVersion: 'v1',
    products: ProductsFeatureConfig.base,
    allowedRedirectDomains: [],
    experts: {
        tracking: {
            lux: {
                appId: 'A0210',
                nameTracker: 'BOT_AI_Experts',
            },
        },
    },
};

// @ts-expect-error no-implicit-any
export function arrayOverwrite(a, b) {
    return Array.isArray(a) ? b : undefined;
}

function getSearchParamsFromUrl(): string {
    const redirectUrl = sessionStorage.getItem('redirect');
    if (!redirectUrl) {
        return window.location.search;
    }
    return new URL(redirectUrl).search;
}

export function extendFeatureConfigFromUrl<T>(features: T): T {
    const featuresFromUrl = new URLSearchParams(getSearchParamsFromUrl()).get('features');

    if (!featuresFromUrl) {
        return features;
    }

    return _.merge({}, features, JSON.parse(featuresFromUrl), arrayOverwrite);
}
